'use es6';

import PropTypes from 'prop-types';
import I18n from 'I18n';
import { getIsEnterprise } from 'ContentData/helpers/blogCreationHelpers';
import { sanitizeSlugInput } from 'ContentUtils/helpers/SlugHelpers';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentSupportedLanguageSelect from 'ContentComponents/components/language/ContentSupportedLanguageSelect';
import Routes from 'ContentUtils/Routes';
import { Link } from 'foundations-components/transitional/components/Link';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { getLibraryTrack } from '../../utils/usageTracking';
import { useBlogQuickCreateRequiredData } from '../../hooks/blogHooks';
import * as blogsClient from 'ContentData/api/Blogs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};
const isCreateDisabled = ({
  name,
  language,
  hasMultiLangBlogAccess,
  metaDescription
}) => {
  const isLanguageRequirementSatisfied = !hasMultiLangBlogAccess || hasMultiLangBlogAccess && language;
  return !name || !isLanguageRequirementSatisfied || !metaDescription;
};
const BlogName = ({
  name,
  onNameChange
}) => {
  const handleNameChange = useCallback(({
    target: {
      value
    }
  }) => onNameChange(value), [onNameChange]);
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogName"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(TextInput, {
      "data-test-id": "blog-modal-name-input",
      value: name,
      onChange: handleNameChange
    })
  });
};
const BlogLanguageSelector = ({
  language,
  hasMultiLangBlogAccess,
  onLanguageChange
}) => {
  const handleLanguageChange = useCallback(({
    target: {
      value
    }
  }) => onLanguageChange(value), [onLanguageChange]);
  if (!hasMultiLangBlogAccess) {
    return null;
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogLanguage"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(ContentSupportedLanguageSelect, {
      value: language,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.blogCreationModal.selectBlogLanguage"
      }),
      onChange: handleLanguageChange
    })
  });
};
const BlogMetaDescription = ({
  metaDescription,
  onMetaDescriptionChange
}) => {
  const handleMetaDescriptionChange = useCallback(({
    target: {
      value
    }
  }) => onMetaDescriptionChange(value), [onMetaDescriptionChange]);
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ContentComponents.blogCreationModal.blogMetaDescription"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(TextInput, {
      "data-test-id": "blog-modal-meta-description-input",
      value: metaDescription,
      onChange: handleMetaDescriptionChange
    })
  });
};
const RequiredDataGuard = ({
  hasRequiredData,
  isRequiredDataLoading,
  children
}) => {
  if (isRequiredDataLoading) {
    return /*#__PURE__*/_jsx(DialogBody, {
      marginBlock: "300",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      })
    });
  }
  if (!hasRequiredData) {
    return /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.blogCreationModal.requiredDataError"
      })
    });
  }
  return children;
};
const BlogCreationModal = ({
  onCancel,
  isBlogCreateLoading,
  onCreate,
  metaDescription,
  onMetaDescriptionChange,
  hasMultiLangBlogAccess,
  onLanguageChange,
  language,
  name,
  onNameChange,
  hasRequiredData,
  isRequiredDataLoading
}) => {
  return /*#__PURE__*/_jsxs(Modal, {
    width: 700,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ContentComponents.blogCreationModal.createBlog"
        })
      })]
    }), /*#__PURE__*/_jsxs(RequiredDataGuard, {
      hasRequiredData: hasRequiredData,
      isRequiredDataLoading: isRequiredDataLoading,
      children: [/*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(BlogName, {
          name: name,
          onNameChange: onNameChange
        }), /*#__PURE__*/_jsx(BlogLanguageSelector, {
          hasMultiLangBlogAccess: hasMultiLangBlogAccess,
          onLanguageChange: onLanguageChange,
          language: language
        }), /*#__PURE__*/_jsx(BlogMetaDescription, {
          metaDescription: metaDescription,
          onMetaDescriptionChange: onMetaDescriptionChange
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "blog-modal-create-button",
          use: "primary",
          onClick: onCreate,
          loading: isBlogCreateLoading,
          disabled: isCreateDisabled({
            name,
            language,
            hasMultiLangBlogAccess,
            metaDescription
          }),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.create"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: onCancel,
          disabled: isBlogCreateLoading,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.cancel"
          })
        }), /*#__PURE__*/_jsx(Link, {
          href: Routes.siteImporter(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ContentComponents.blogCreationModal.import"
          })
        })]
      })]
    })]
  });
};
const useCreateBlogAndBlogPost = (onSuccess, onFail) => {
  const [isLoading, setIsLoading] = useState(false);
  const create = useCallback((blogData, blogPostData) => {
    setIsLoading(true);
    blogsClient.createBlog(blogData).then(blog => blogsClient.createBlogPost(blog.id, blogPostData).then(blogPost => ({
      blog,
      blogPost
    }))).then(({
      blog,
      blogPost
    }) => {
      setIsLoading(false);
      onSuccess(blog, blogPost);
    }).catch(err => {
      setIsLoading(false);
      onFail(err);
    });
  }, [onFail, onSuccess]);
  return {
    isLoading,
    create
  };
};
const blogCreationErrorSwitch = __error => {
  return {
    message: I18n.text('ContentComponents.blogCreationModal.errors.default.message'),
    titleText: I18n.text('ContentComponents.blogCreationModal.errors.default.title'),
    type: 'danger'
  };
};
export const QuickBlogCreationModal = ({
  onBlogCreateSuccess = noop,
  onBlogCreateFail = noop,
  onCancel
}) => {
  const {
    isLoading: isRequiredDataLoading,
    requiredData: {
      domain,
      blogTemplate,
      blogListingTemplate,
      defaultLanguage,
      scopes
    }
  } = useBlogQuickCreateRequiredData();
  const hasRequiredData = Boolean(domain) && Boolean(blogListingTemplate) && Boolean(blogTemplate) && Boolean(defaultLanguage);
  const track = getLibraryTrack();
  const handleBlogCreateFail = useCallback(error => {
    onBlogCreateFail(error);
    FloatingAlertStore.addAlert(blogCreationErrorSwitch(error));
  }, [onBlogCreateFail]);
  const {
    isLoading: isBlogCreateLoading,
    create: blogCreate
  } = useCreateBlogAndBlogPost(onBlogCreateSuccess, handleBlogCreateFail);
  const [name, setName] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [overrideLanguage, setOverrideLanguage] = useState('');
  const language = overrideLanguage || defaultLanguage;
  const isEnterprise = useMemo(() => getIsEnterprise(scopes), [scopes]);
  const blog = useMemo(() => {
    const blogName = name.trim();
    return {
      name: blogName,
      domain,
      language,
      slug: sanitizeSlugInput(blogName),
      itemTemplatePath: blogTemplate,
      listingTemplatePath: blogListingTemplate,
      htmlTitle: blogName,
      description: metaDescription
    };
  }, [name, blogTemplate, blogListingTemplate, metaDescription, domain, language]);
  const blogPost = useMemo(() => {
    return {
      language,
      name: I18n.text('ContentComponents.blogCreationModal.defaultPostTitle')
    };
  }, [language]);
  const onClickCreate = useCallback(() => {
    blogCreate(blog, blogPost);
    track('blogQuickCreateModalClickedCreate');
  }, [blog, blogCreate, blogPost, track]);
  useEffect(() => {
    track('blogQuickCreateModalOpened');
  }, [track]);
  return /*#__PURE__*/_jsx(BlogCreationModal, {
    hasMultiLangBlogAccess: true,
    isEnterprise: isEnterprise,
    isRequiredDataLoading: isRequiredDataLoading,
    hasRequiredData: hasRequiredData,
    isBlogCreateLoading: isBlogCreateLoading,
    language: language,
    onLanguageChange: setOverrideLanguage,
    name: name,
    metaDescription: metaDescription,
    onNameChange: setName,
    onMetaDescriptionChange: setMetaDescription,
    onCancel: onCancel,
    onCreate: onClickCreate,
    shouldRenderImportLink: true
  });
};
QuickBlogCreationModal.propTypes = {
  onBlogCreateSuccess: PropTypes.func,
  onBlogCreateFail: PropTypes.func,
  onCancel: PropTypes.func
};